<template>
  <div class="add-edit-ptc-page">
    <!-- ===================== Hero Header ===================== -->
    <div class="ptc-hero">
      <div class="ptc-hero__blob ptc-hero__blob--one" />
      <div class="ptc-hero__blob ptc-hero__blob--two" />

      <div class="ptc-hero__content">
        <div class="ptc-hero__icon">
          <feather-icon
            icon="LinkIcon"
            size="30"
          />
        </div>
        <div>
          <h2 class="ptc-hero__title">
            {{ isEdit ? 'Edit Product Link' : 'Add Product To Certificate' }}
          </h2>
          <p class="ptc-hero__subtitle">
            Connect a certificate to the product categories it applies to.
          </p>
        </div>
      </div>

      <button
        type="button"
        class="ptc-back-btn"
        @click="goBack"
      >
        <span class="ptc-back-btn__icon">
          <feather-icon
            icon="ArrowLeftIcon"
            size="16"
          />
        </span>
        <span class="text-nowrap">Back</span>
      </button>
    </div>

    <!-- ===================== Form ===================== -->
    <b-card
      no-body
      class="ptc-form-card"
    >
      <validation-observer
        ref="infoRules"
        v-slot="{ invalid }"
        tag="form"
      >
        <b-card-body class="p-2 p-md-3">
          <b-row>
            <!-- Step 1: Main certificate -->
            <b-col
              cols="12"
              lg="6"
            >
              <div class="ptc-field-label">
                <span class="ptc-field-label__icon ptc-field-label__icon--1">
                  <feather-icon
                    icon="AwardIcon"
                    size="15"
                  />
                </span>
                <span>Certificate</span>
              </div>

              <validation-provider
                #default="validationContext"
                name="Main Certificate"
                rules="required"
              >
                <b-form-group>
                  <b-form-select
                    v-model="selectedGroupId"
                    :options="mainCertificate"
                    placeholder="Select a certificate"
                    class="d-inline-block w-100 ptc-select"
                    :state="getValidationState(validationContext)"
                    @input="onGroupChange"
                  />
                  <b-form-invalid-feedback>
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>

            <!-- Step 2: Sub certificate -->
            <b-col
              v-if="showSelectedSupCertificates"
              cols="12"
              lg="6"
            >
              <div class="ptc-field-label">
                <span class="ptc-field-label__icon ptc-field-label__icon--2">
                  <feather-icon
                    icon="LayersIcon"
                    size="15"
                  />
                </span>
                <span>Sub Certificate</span>
              </div>

              <b-form-group>
                <b-form-select
                  id="status_id_cer"
                  v-model="selectedSupCertificates"
                  class="d-flex flex-column ptc-select"
                  :options="supCertificate"
                  trim
                  placeholder="Select a sub certificate"
                  @change="onSupChange"
                />
              </b-form-group>
            </b-col>

            <!-- Step 3: Products -->
            <b-col
              v-if="showSelectedProdect"
              cols="12"
            >
              <hr class="ptc-divider">

              <div class="ptc-field-label">
                <span class="ptc-field-label__icon ptc-field-label__icon--3">
                  <feather-icon
                    icon="PackageIcon"
                    size="15"
                  />
                </span>
                <span>Linked Product Categories</span>
                <b-badge
                  pill
                  variant="light-primary"
                  class="ml-50"
                >
                  {{ prodectList.length }}
                </b-badge>
              </div>

              <b-form-group>
                <v-select
                  id="status_id_cer"
                  v-model="selectedProduct"
                  class="d-flex flex-column ptc-select ptc-select--v"
                  :options="filteredOptions"
                  trim
                  placeholder="Search and select a product category to add"
                  label="text"
                  :searchable="true"
                  @input="addProduct"
                />
              </b-form-group>

              <transition-group
                v-if="prodectList.length"
                tag="ul"
                name="ptc-chip"
                class="ptc-product-chips"
              >
                <li
                  v-for="product in prodectList"
                  :key="product.value"
                  class="ptc-product-chip"
                >
                  <span class="ptc-product-chip__icon">
                    <feather-icon
                      icon="PackageIcon"
                      size="13"
                    />
                  </span>
                  <span class="ptc-product-chip__text">{{ product.text }}</span>
                  <button
                    type="button"
                    class="ptc-product-chip__remove"
                    title="Remove"
                    @click="removeProduct(product)"
                  >
                    <feather-icon
                      icon="XIcon"
                      size="13"
                    />
                  </button>
                </li>
              </transition-group>
              <div
                v-else
                class="ptc-empty-products"
              >
                <feather-icon
                  icon="InboxIcon"
                  size="16"
                  class="mr-50"
                />
                No products linked yet. Search above to add some.
              </div>
            </b-col>

            <!-- Server Errors -->
            <b-col
              v-if="Object.values(errors).length > 0"
              cols="12"
            >
              <div class="ptc-error-box">
                <feather-icon
                  icon="AlertTriangleIcon"
                  size="16"
                  class="mr-50"
                />
                <div>
                  <p
                    v-for="(error, key, index) in errors"
                    :key="index"
                    class="mb-0"
                  >
                    {{ error.toString() }}
                  </p>
                </div>
              </div>
            </b-col>
          </b-row>

          <div class="ptc-actions">
            <button
              v-if="!loader"
              type="button"
              class="ptc-submit-btn"
              :disabled="invalid || Object.values(errors).length > 0"
              @click="addEditProToCer()"
            >
              <feather-icon
                :icon="isEdit ? 'CheckIcon' : 'PlusIcon'"
                size="16"
                class="mr-50"
              />
              {{ isEdit ? 'Save Changes' : 'Add' }}
            </button>
            <button
              v-else
              type="button"
              class="ptc-submit-btn ptc-submit-btn--loading"
              disabled
            >
              <b-spinner
                small
                class="mr-50"
              />
              Loading...
            </button>
          </div>
        </b-card-body>
      </validation-observer>
    </b-card>
  </div>
</template>

<script>
import formValidation from '@core/comp-functions/forms/form-validation'
import axios from 'axios'
import { required } from '@validations'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import VSelect from 'vue-select'
import decryptData from '@/store/auth/decryptData'

export default {
  components: {
    VSelect,
  },
  data() {
    return {
      id: this.$store.state.generalIds.id,
      required,
      loader: false,
      validationErrors: {},
      errors: {},
      mainCertificate: [],
      selectedGroupId: '',
      supCertificate: [],
      selectedSupCertificates: '',
      showSelectedSupCertificates: false,
      allProdect: [],
      showSelectedProdect: false,
      prodectList: [],
      selectedProduct: null,
    }
  },
  computed: {
    filteredOptions() {
      return this.allProdect.filter(
        product => !this.prodectList.some(selected => selected.value === product.value),
      )
    },
    isEdit() {
      return Boolean(this.$route.params.id)
    },
  },
  setup() {
    const { getValidationState } = formValidation()
    return {
      getValidationState,
    }
  },
  watch: {
    selectedGroupId: {
      handler() {
        this.onGroupChange()
      },
      immediate: true,
    },
  },
  mounted() {
    this.getAllCertificate()
    this.getAllProdect()
    setTimeout(() => {
      this.getProdectDataFromCertificate()
    }, 500)
  },
  methods: {
    goBack() {
      this.$router.push({ name: 'certificate-product' })
    },
    getProdectDataFromCertificate() {
      if (this.$route.params.id) {
        axios.get(`certificate-with-products/${this.$route.params.id}`).then(res => {
          const { data } = decryptData(res.data.payload)
          this.selectedGroupId = data.certificate_group.id
          this.showSelectedSupCertificates = true
          this.selectedSupCertificates = data.id
          this.showSelectedProdect = true
          this.prodectList = data.product_categories.map(item => ({
            value: item.id,
            text: item.name,
          }))
        })
      }
    },
    addProduct() {
      if (this.selectedProduct) {
        const product = this.allProdect.find(opt => opt.value === this.selectedProduct.value)
        if (product) {
          this.prodectList.push(product)
          this.selectedProduct = null
        }
      }
    },
    removeProduct(product) {
      this.prodectList = this.prodectList.filter(p => p.value !== product.value)
    },
    getAllProdect() {
      axios.get('product-categories').then(res => {
        this.allProdect = decryptData(res.data.payload).data.productCategories.map(item => ({
          value: item.id,
          text: item.name,
        }))
      })
    },
    getAllCertificate() {
      axios.get('certificate').then(res => {
        this.mainCertificate = decryptData(res.data.payload).data.groups.map(item => ({
          value: item.id,
          text: `${item.name.ar} - ${item.name.en}`,
          certificates: item.certificates,
        }))
      })
    },
    onGroupChange() {
      this.showSelectedSupCertificates = true
      const group = this.mainCertificate.find(g => g.value === this.selectedGroupId)
      this.supCertificate = group ? group.certificates.map(item => ({
        value: item.id,
        text: `${item.name.ar} - ${item.name.en}`,
      })) : []
    },
    onSupChange() {
      this.showSelectedProdect = true
    },
    addEditProToCer() {
      if (this.$route.params.id) {
        this.loader = true

        const formData = new FormData()
        formData.append('_method', 'PUT')

        if (this.prodectList.length > 0) {
          this.prodectList.forEach((file, index) => formData.append(`product_ids[${index}]`, file.value))
        }

        axios
          .post(`certificates/${this.selectedSupCertificates}/products`, formData)
          .then(res => {
            if (res.status === 200 || res.status === 201) {
              this.$router.push({ name: 'certificate-product' })
              this.$toasted.show('Created Successfully', {
                position: 'top-center',
                duration: 3000,
              })
            }
          })
          .catch(error => {
            if (error.response.status) {
              const payload = decryptData(error.response.data.payload).message
              const firstArrayKey = Object.keys(payload).find(key => Array.isArray(payload[key]))
              const errorMessage = firstArrayKey ? payload[firstArrayKey][0] : 'An unknown error occurred'
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: `${errorMessage}`,
                  icon: 'BellIcon',
                  variant: 'error',
                },
              })
            }
          })
          .finally(() => {
            this.loader = false
          })
      } else {
        this.loader = true

        const formData = new FormData()

        if (this.prodectList.length > 0) {
          this.prodectList.forEach((file, index) => formData.append(`product_ids[${index}]`, file.value))
        }

        axios
          .post(`certificates/${this.selectedSupCertificates}/products`, formData)
          .then(res => {
            if (res.status === 200 || res.status === 201) {
              this.$router.push({ name: 'certificate-product' })
              this.$toasted.show('Created Successfully', {
                position: 'top-center',
                duration: 3000,
              })
            }
          })
          .catch(error => {
            if (error.response.status) {
              const payload = decryptData(error.response.data.payload).message
              const firstArrayKey = Object.keys(payload).find(key => Array.isArray(payload[key]))
              const errorMessage = firstArrayKey ? payload[firstArrayKey][0] : 'An unknown error occurred'
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: `${errorMessage}`,
                  icon: 'BellIcon',
                  variant: 'error',
                },
              })
            }
          })
          .finally(() => {
            this.loader = false
          })
      }
    },
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>

<style lang="scss" scoped src="./AddEditProductToCertificate.scss"></style>
